import React, { useEffect, useRef } from 'react';
import './Dither.css';

interface DitherProps {
  waveSpeed?: number;
  waveFrequency?: number;
  waveAmplitude?: number;
  waveColor?: [number, number, number];
  colorNum?: number;
  pixelSize?: number;
  disableAnimation?: boolean;
  enableMouseInteraction?: boolean;
  mouseRadius?: number;
}

/*
 * Same wave + ordered-dither look as the original WebGL/three.js version,
 * rebuilt on plain 2D Canvas. WebGL context creation was confirmed broken
 * in the target environment (fails even for external demo sites in a
 * clean Incognito window), so this avoids depending on a WebGL context
 * existing at all: getContext('2d') has no comparable failure mode.
 */

// Classic Perlin-style gradient noise (deterministic permutation table),
// much closer in character to the original shader's `cnoise` than simple
// value noise: value noise looks "blobby", gradient noise flows/swirls.
const PERLIN_PERM: Uint8Array = (() => {
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  let seed = 1234;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = p[i]; p[i] = p[j]; p[j] = tmp;
  }
  const perm = new Uint8Array(512);
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
  return perm;
})();

function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
  return a + t * (b - a);
}

function grad(hash: number, x: number, y: number): number {
  const h = hash & 7;
  const u = h < 4 ? x : y;
  const v = h < 4 ? y : x;
  return ((h & 1) !== 0 ? -u : u) + ((h & 2) !== 0 ? -2 * v : 2 * v);
}

function perlin2(x: number, y: number): number {
  const X = Math.floor(x) & 255;
  const Y = Math.floor(y) & 255;
  const xf = x - Math.floor(x);
  const yf = y - Math.floor(y);
  const u = fade(xf);
  const v = fade(yf);
  const aa = PERLIN_PERM[PERLIN_PERM[X] + Y];
  const ab = PERLIN_PERM[PERLIN_PERM[X] + Y + 1];
  const ba = PERLIN_PERM[PERLIN_PERM[X + 1] + Y];
  const bb = PERLIN_PERM[PERLIN_PERM[X + 1] + Y + 1];
  const x1 = lerp(grad(aa, xf, yf), grad(ba, xf - 1, yf), u);
  const x2 = lerp(grad(ab, xf, yf - 1), grad(bb, xf - 1, yf - 1), u);
  return lerp(x1, x2, v); // roughly -1..1
}

function fbm(x: number, y: number, amplitude: number, frequency: number): number {
  let value = 0;
  let amp = 1;
  let fx = x;
  let fy = y;
  for (let i = 0; i < 3; i++) {
    value += amp * Math.abs(perlin2(fx, fy));
    fx *= frequency;
    fy *= frequency;
    amp *= amplitude;
  }
  return value;
}

// 4x4 Bayer ordered-dither matrix, normalized to 0..1
const BAYER_4X4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map(v => v / 16);

export default function Dither({
  waveSpeed = 0.05,
  waveFrequency = 3,
  waveAmplitude = 0.3,
  waveColor = [0.5, 0.5, 0.5],
  colorNum = 4,
  pixelSize = 4,
  disableAnimation = false,
  enableMouseInteraction = true,
  mouseRadius = 0.3,
}: DitherProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let time = 0;
    let width = 1;
    let height = 1;
    let dpr = 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(1, Math.floor(rect.width * dpr));
      height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.width = width;
      canvas.height = height;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const handlePointerMove = (e: PointerEvent) => {
      if (!enableMouseInteraction) return;
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
        // Not flipped: canvas pixel-space is already top-down, matching how
        // `v` below is computed. Flipping here (to mirror the original
        // shader's GL-convention flip) was the actual cause of the cursor
        // distortion moving vertically inverted from the real pointer.
        y: ((e.clientY - rect.top) / rect.height) * 2 - 1,
      };
    };
    const handlePointerLeave = () => { mouseRef.current = null; };
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerleave', handlePointerLeave);

    const [r, g, b] = waveColor;

    const draw = () => {
      if (!disableAnimation) time += 0.016 * waveSpeed;

      const cell = Math.max(2, pixelSize * dpr);
      const cols = Math.ceil(width / cell);
      const rows = Math.ceil(height / cell);
      const aspect = width / height;

      for (let cy = 0; cy < rows; cy++) {
        for (let cx = 0; cx < cols; cx++) {
          const px = cx * cell;
          const py = cy * cell;

          let u = px / width - 0.5;
          let v = py / height - 0.5;
          u *= aspect;

          // Our footer strip is a much wider/thinner aspect ratio than the
          // reference's square-ish demo box, so the same coordinates would
          // otherwise cram in many more pattern cycles horizontally and
          // read as busy/grainy rather than a few smooth flowing shapes.
          // Scaling the sample point down (zooming in on the noise field)
          // compensates for that.
          const NOISE_SCALE = 0.45;
          const su = u * NOISE_SCALE;
          const sv = v * NOISE_SCALE;

          const warp = fbm(su - time, sv - time, waveAmplitude, waveFrequency);
          let f = fbm(su + warp, sv + warp, waveAmplitude, waveFrequency);

          if (enableMouseInteraction && mouseRef.current) {
            const mx = mouseRef.current.x * aspect * 0.5;
            const my = mouseRef.current.y * 0.5;
            const dx = u - mx;
            const dy = v - my;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const effect = 1 - Math.min(1, dist / mouseRadius);
            f -= 0.5 * Math.max(0, effect);
          }

          const bx = cx % 4;
          const by = cy % 4;
          const threshold = BAYER_4X4[by * 4 + bx] - 0.25;
          const step = 1 / (colorNum - 1);
          let q = f + threshold * step;
          q = Math.max(0, Math.min(1, q - 0.2));
          q = Math.round(q * (colorNum - 1)) / (colorNum - 1);

          ctx.fillStyle = `rgb(${Math.round(r * q * 255)}, ${Math.round(g * q * 255)}, ${Math.round(b * q * 255)})`;
          ctx.fillRect(px, py, cell, cell);
        }
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, [waveSpeed, waveFrequency, waveAmplitude, waveColor, colorNum, pixelSize, disableAnimation, enableMouseInteraction, mouseRadius]);

  return <canvas ref={canvasRef} className="dither-container" />;
}
